import { Button } from "@/components/ui/Button";
import { holdKeyboard } from "../../lib/holdKeyboard";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { noteEffectiveOrder, manualOrderBetween } from "@caliu/utils";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import i18n from "../../lib/i18n";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useNoteListNav } from "../../hooks/useNoteListNav";
import { useNoteLimit } from "../../hooks/useNoteLimit";
import type { NoteView } from "../../hooks/useNotes";
import {
  useCreateNote,
  useDeleteNote,
  useNoteIds,
  useNotesMap,
  usePinNote,
  useSetNoteOrder,
} from "../../hooks/useNotes";
import { useArchiveNote } from "../../hooks/useArchive";
import { useSharedStubsMap } from "../../hooks/useSharedWithMe";
import { useTags } from "../../hooks/useTags";
import { bulkExport } from "../../lib/bulkActions";
import { getNote, type LocalNote } from "../../lib/db";
import type { ExportFormat } from "../../lib/export";
import { Sentry } from "../../lib/sentry";
import type { ProjectedAttachment } from "../../lib/attachmentProjection";
import { useIsPro } from "../../store/subscription";
import { setPrefetchedNote } from "../../lib/notePrefetch";
import { useSQLiteQuery } from "../../lib/reactive";
import { query } from "../../lib/sqlite";
import { formatKeyCombo } from "../../lib/shortcuts";
import { tagFullPath } from "../../lib/tagHierarchy";
import { newNoteContent, newNoteTagPath } from "../../lib/newNoteContent";
import { extractTitleFromContent } from "../../hooks/useEditorAutosave";
import { useShortcutsStore } from "../../store/shortcuts";
import { useSelectionStore } from "../../store/selection";
import { useUIStore } from "../../store/ui";
import { NoteActionsMenu } from "../NoteActionsMenu";
import { SharedNoteActionsMenu } from "../SharedNoteActionsMenu";
import { TagEditor } from "../TagEditor";
import { TagTitle } from "../../lib/tagIcons";
import { BulkActionsBar } from "./BulkActionsBar";
import { NoteColumnHeader } from "./NoteColumnHeader";
import { NoteItem } from "./NoteItem";
import { NoteListMenu } from "./NoteListMenu";
import { TagRenameDialog } from "./Sidebar";
import { useLongPress } from "../../hooks/useLongPress";
import { Icon } from "@/components/ui/Icon";
import { CaliuLogo } from "@/components/ui/CaliuLogo";

interface NoteListProps {
  tagId?: string;
  view?: NoteView;
  leftAction?: React.ReactNode;
  /** Room at the bottom of the list for the phone search field. */
  bottomInset?: boolean;
}

const VIEW_LABEL_KEYS: Record<NoteView, string> = {
  all: "views.all",
  pinned: "views.pinned",
  todo: "views.todo",
  today: "views.today",
  untagged: "views.untagged",
  thisWeek: "views.thisWeek",
  shared: "views.shared",
  sharedWithMe: "views.sharedWithMe",
  attachments: "views.attachments",
};

/** Relative date label */
/** New Note: the theme accent as tinted glass (translucent, blurred, hairline edge), legible in light and dark. */
const ACCENT_GLASS =
  "bg-accent-glass hover:bg-accent-glass-hover backdrop-blur-xl border border-accent-glass-edge";

export function formatRelativeDate(dateStr: string) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  const diffDays = Math.floor(diffMs / 86_400_000);
  const locale = i18n.resolvedLanguage || i18n.language || "en";

  if (diffMins < 1) return i18n.t("noteList.justNow");
  if (diffMins < 60) return i18n.t("noteList.minutesAgo", { count: diffMins });
  if (diffDays === 0) return i18n.t("noteList.today");
  if (diffDays === 1) return i18n.t("noteList.yesterday");
  if (diffDays < 7) return date.toLocaleDateString(locale, { weekday: "short" });
  if (diffDays < 365)
    return date.toLocaleDateString(locale, {
      month: "short",
      day: "numeric",
    });
  return date.toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
    year: "2-digit",
  });
}

/* ── Note item ───────────────────────────────────────────── */
const NoteListItem = memo(
  function NoteListItem({
    note,
    isActive,
    isSelected,
    isSelectionMode,
    onDelete,
    onSelect,
    previewStyle,
    attachments,
    // `attachmentsKey` is intentionally not destructured here — it's read off
    // the props object by the memo comparator below, not used in the body.
    mergesTop,
    mergesBottom,
    showSeparatorBelow,
    dateProp,
  }: {
    note: LocalNote;
    isActive: boolean;
    isSelected: boolean;
    isSelectionMode: boolean;
    onDelete: (noteId: string) => void;
    onSelect: (noteId: string, e: React.MouseEvent | null) => void;
    previewStyle?: "small" | "medium" | "large";
    attachments?: ProjectedAttachment[];
    attachmentsKey?: string;
    mergesTop?: boolean;
    mergesBottom?: boolean;
    showSeparatorBelow?: boolean;
    dateProp?: "updatedAt" | "createdAt";
  }) {
    const noteId = note.id;
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const pinNote = usePinNote();
    const archiveNote = useArchiveNote();
    const isMobile = useIsMobile();
    const toggle = useSelectionStore((s) => s.toggle);
    const setSelectionMode = useSelectionStore((s) => s.setSelectionMode);

    // Long-press context menu state
    const [contextMenuOpen, setContextMenuOpen] = useState(false);
    const [contextMenuPos, setContextMenuPos] = useState({ x: 0, y: 0 });
    const triggerRef = useRef<HTMLButtonElement>(null);

    const isSharedStub = !!note.sharedWithMe;

    const handleClick = useCallback(
      (e?: React.MouseEvent) => {
        if (!note) return;
        // Shared-with-me stubs are excluded from bulk selection (they don't
        // live in the notes table) — every click just navigates.
        if (!isSharedStub) {
          // Selection mode active: any click toggles selection
          if (isSelectionMode) {
            if (e?.shiftKey) {
              onSelect(noteId, e);
            } else {
              toggle(noteId);
            }
            return;
          }

          // Desktop: Cmd/Ctrl+Click or Shift+Click → enter selection
          if (e && (e.metaKey || e.ctrlKey || e.shiftKey)) {
            e.preventDefault();
            onSelect(noteId, e);
            return;
          }
        }

        // Normal click → navigate
        if (isActive) {
          const qs = searchParams.toString();
          navigate(`/app${qs ? `?${qs}` : ""}`, { replace: true });
        } else {
          if (!isSharedStub) setPrefetchedNote(note);
          const qs = searchParams.toString();
          navigate(`/app/note/${noteId}${qs ? `?${qs}` : ""}`);
        }
      },
      [
        note,
        noteId,
        isActive,
        isSharedStub,
        navigate,
        searchParams,
        isSelectionMode,
        toggle,
        onSelect,
      ],
    );

    const longPress = useLongPress(
      useCallback(
        (pos) => {
          // In mobile: long-press enters selection mode (or opens context menu if already in selection mode)
          // Shared-with-me stubs can't be selected — long-press opens their
          // limited context menu instead.
          if (isMobile && !isSelectionMode && !isSharedStub) {
            setSelectionMode(true);
            toggle(noteId);
            return;
          }

          // Position dropdown centered on the note element, not at touch point
          const noteEl = document
            .querySelector(`[data-note-id="${noteId}"]`)
            ?.closest("[class*=group]");
          if (noteEl) {
            const rect = noteEl.getBoundingClientRect();
            setContextMenuPos({
              x: rect.left + rect.width / 2, // horizontal center
              y: rect.bottom, // bottom edge
            });
          } else {
            setContextMenuPos(pos);
          }
          setContextMenuOpen(true);
        },
        [noteId, isMobile, isSelectionMode, isSharedStub, setSelectionMode, toggle],
      ),
    );

    const handlePin = () => {
      pinNote.mutate({ id: noteId, isPinned: !note.isPinned });
    };

    const handleDelete = () => {
      onDelete(noteId);
    };

    const handleArchive = () => {
      archiveNote.mutate(noteId);
      // If the archived note is open, navigate away — it's now hidden from this list.
      if (isActive) {
        const qs = searchParams.toString();
        navigate(`/app${qs ? `?${qs}` : ""}`, { replace: true });
      }
    };

    const menuContent = isSharedStub ? (
      <SharedNoteActionsMenu
        noteId={noteId}
        onRemoved={() => {
          if (isActive) {
            const qs = searchParams.toString();
            navigate(`/app${qs ? `?${qs}` : ""}`, { replace: true });
          }
        }}
      />
    ) : (
      <NoteActionsMenu
        note={note}
        onPin={handlePin}
        onArchive={handleArchive}
        onDelete={handleDelete}
        onLeft={() => {
          if (!isActive) return;
          const qs = searchParams.toString();
          navigate(`/app${qs ? `?${qs}` : ""}`, { replace: true });
        }}
        onSelect={() => toggle(noteId)}
      />
    );

    return (
      <>
        <NoteItem
          note={note}
          isActive={isActive || contextMenuOpen}
          isSelected={isSelected}
          isSelectionMode={isSelectionMode}
          dateLabel={formatRelativeDate(
            dateProp === "createdAt" ? note.createdAt : note.updatedAt,
          )}
          previewStyle={previewStyle}
          attachments={attachments}
          mergesTop={mergesTop}
          mergesBottom={mergesBottom}
          showSeparatorBelow={showSeparatorBelow}
          onClick={handleClick}
          onTouchHandlers={isMobile ? { ...longPress } : undefined}
          dropdownContent={
            !isMobile && !isSelectionMode ? menuContent : undefined
          }
        />

        {/* Long-press context menu (mobile only, not in selection mode) */}
        {isMobile && !isSelectionMode && (
          <DropdownMenu
            open={contextMenuOpen}
            onOpenChange={setContextMenuOpen}
          >
            <DropdownMenuTrigger asChild>
              <span
                ref={triggerRef}
                className="fixed w-0 h-0 pointer-events-none"
                style={{
                  left: contextMenuPos.x,
                  top: contextMenuPos.y,
                }}
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center" side="bottom" sideOffset={4}>
              {menuContent}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </>
    );
  },
  (prev, next) =>
    prev.note === next.note &&
    prev.isActive === next.isActive &&
    prev.isSelected === next.isSelected &&
    prev.isSelectionMode === next.isSelectionMode &&
    prev.previewStyle === next.previewStyle &&
    prev.attachmentsKey === next.attachmentsKey &&
    prev.mergesTop === next.mergesTop &&
    prev.mergesBottom === next.mergesBottom &&
    prev.showSeparatorBelow === next.showSeparatorBelow &&
    prev.dateProp === next.dateProp,
);

/* ── Empty state ─────────────────────────────────────────── */
function EmptyNoteList({ view }: { view?: NoteView }) {
  const { t } = useTranslation();
  const key = view ?? "";
  const title = view
    ? t(`noteList.empty_${key}`, { defaultValue: t("noteList.empty") })
    : t("noteList.empty");
  const subtitle = view
    ? t(`noteList.empty_${key}_subtitle`, {
        defaultValue: t("noteList.emptySubtitle"),
      })
    : t("noteList.emptySubtitle");

  return (
    <div data-list-empty className="flex flex-col items-center justify-center p-12 gap-[6px] h-full text-center">
      <CaliuLogo size={42} className="text-accent opacity-[0.82] mb-2" />
      <p className="text-sm font-medium text-muted m-0">{title}</p>
      <p className="text-xs text-placeholder m-0">{subtitle}</p>
    </div>
  );
}

/* ── Estimated row heights per preview style (for initial render) ── */
const ESTIMATE_MAP: Record<string, number> = {
  small: 52,
  medium: 96,
  large: 140,
};

// Cap attachments fetched/rendered per note row, to bound list work.
const NOTE_ATTACHMENT_CAP = 8;

/**
 * Scrolls the list to the open note whenever a different note opens
 * (keyboard navigation, the command palette, a link), and on mount, since
 * coming back from Archive or Trash remounts the list. The reveal waits until
 * the note is in `noteIds`, so a list that fills in later still gets it.
 */
function useRevealActiveNote(
  activeNoteId: string | undefined,
  noteIds: string[],
  scrollToIndex: (index: number) => void,
) {
  const revealedRef = useRef<string | undefined>(undefined);
  const scrollRef = useRef(scrollToIndex);
  scrollRef.current = scrollToIndex;
  useEffect(() => {
    if (!activeNoteId) {
      revealedRef.current = undefined;
      return;
    }
    if (activeNoteId === revealedRef.current) return;
    const index = noteIds.indexOf(activeNoteId);
    if (index < 0) return;
    // A frame later: on mount the virtualizer only adopts its scroll element in
    // its own effects, and a scroll issued before that is lost.
    const frame = requestAnimationFrame(() => {
      scrollRef.current(index);
      revealedRef.current = activeNoteId;
    });
    return () => cancelAnimationFrame(frame);
  }, [activeNoteId, noteIds]);
}

/* ── Virtualized note list ────────────────────────────────── */
function VirtualizedNoteList({
  noteIds,
  notesMap,
  activeNoteId,
  selectedIds,
  isSelectionMode,
  onDelete,
  onSelect,
  notesAttachments,
  previewStyle = "large",
  dateProp,
  view,
  extraBottomPadding,
  footer,
}: {
  noteIds: string[];
  notesMap: Map<string, LocalNote>;
  activeNoteId?: string;
  selectedIds: Set<string>;
  isSelectionMode: boolean;
  onDelete: (noteId: string) => void;
  onSelect: (noteId: string, e: React.MouseEvent | null) => void;
  notesAttachments?: Map<string, ProjectedAttachment[]>;
  previewStyle?: "small" | "medium" | "large";
  dateProp?: "updatedAt" | "createdAt";
  view?: NoteView;
  extraBottomPadding?: boolean;
  footer?: React.ReactNode;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const estimateSize = useCallback(
    () => ESTIMATE_MAP[previewStyle] ?? 92,
    [previewStyle],
  );

  const getItemKey = useCallback((index: number) => noteIds[index], [noteIds]);

  const virtualizer = useVirtualizer({
    count: noteIds.length,
    getScrollElement: () => scrollRef.current,
    estimateSize,
    getItemKey,
    overscan: 30,
  });

  useRevealActiveNote(activeNoteId, noteIds, (index) =>
    virtualizer.scrollToIndex(index, { align: "auto" }),
  );

  if (noteIds.length === 0) {
    return (
      <div className="flex flex-col flex-1 overflow-y-auto p-2">
        <EmptyNoteList view={view} />
      </div>
    );
  }

  return (
    <div
      ref={scrollRef}
      className={cn(
        "flex-1 overflow-y-auto p-2",
        extraBottomPadding && "pb-24",
      )}
    >
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          position: "relative",
          width: "100%",
        }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const i = virtualRow.index;
          const id = noteIds[i];
          const note = notesMap.get(id);
          if (!note) return null;
          const isActive = id === activeNoteId;
          const isSelected = selectedIds.has(id);
          const attachments = notesAttachments?.get(id);
          // Content fingerprint so the memo'd row only re-renders when its
          // attachment set actually changes (the Map is rebuilt each notify).
          const attachmentsKey = attachments
            ? attachments.map((a) => `${a.kind[0]}${a.src}`).join("|")
            : "";
          // Flat-list adjacency (single column = render order): merge the shared
          // corner between two consecutive selected rows, and hide the hairline
          // separator under any highlight fill (this row or the next).
          const nextId = noteIds[i + 1];
          const prevSelected = i > 0 && selectedIds.has(noteIds[i - 1]);
          const nextSelected = !!nextId && selectedIds.has(nextId);
          const nextHighlighted =
            !!nextId && (nextId === activeNoteId || selectedIds.has(nextId));
          const mergesTop = isSelected && prevSelected;
          const mergesBottom = isSelected && nextSelected;
          const showSeparatorBelow =
            !!nextId && !(isActive || isSelected) && !nextHighlighted;
          return (
            <div
              key={id}
              data-index={virtualRow.index}
              ref={virtualizer.measureElement}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              <NoteListItem
                note={note}
                isActive={isActive}
                isSelected={isSelected}
                isSelectionMode={isSelectionMode}
                onDelete={onDelete}
                onSelect={onSelect}
                previewStyle={previewStyle}
                attachments={attachments}
                attachmentsKey={attachmentsKey}
                mergesTop={mergesTop}
                mergesBottom={mergesBottom}
                showSeparatorBelow={showSeparatorBelow}
                dateProp={dateProp}
              />
            </div>
          );
        })}
      </div>
      {footer}
    </div>
  );
}

/* ── Sortable (manual-order) note list ───────────────────────────────────
 * Drag-to-reorder for the "Manual" sort mode. dnd-kit doesn't compose cleanly
 * with the virtualizer's absolute transforms, so manual mode renders a plain
 * (non-virtualized) list instead. Manual-order lists are user-curated and
 * small, so rendering every row is fine.
 * ponytail: non-virtualized. If someone hand-orders thousands of notes and it
 * lags, virtualize with a dnd-kit + react-virtual integration then. */
type SortableRowProps = Omit<
  React.ComponentProps<typeof NoteListItem>,
  "note"
> & { id: string; note: LocalNote };

function SortableNoteRow({ id, ...itemProps }: SortableRowProps) {
  // Shared-with-me stubs have no manual sort_order — not draggable.
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id, disabled: !!itemProps.note.sharedWithMe });
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : undefined,
      }}
      className={cn(isDragging && "relative z-10")}
      {...attributes}
      {...listeners}
    >
      <NoteListItem {...itemProps} />
    </div>
  );
}

function SortableNoteList(props: {
  noteIds: string[];
  notesMap: Map<string, LocalNote>;
  activeNoteId?: string;
  selectedIds: Set<string>;
  isSelectionMode: boolean;
  onDelete: (noteId: string) => void;
  onSelect: (noteId: string, e: React.MouseEvent | null) => void;
  notesAttachments?: Map<string, ProjectedAttachment[]>;
  previewStyle?: "small" | "medium" | "large";
  dateProp?: "updatedAt" | "createdAt";
  view?: NoteView;
  extraBottomPadding?: boolean;
  footer?: React.ReactNode;
}) {
  const {
    noteIds,
    notesMap,
    activeNoteId,
    selectedIds,
    isSelectionMode,
    onDelete,
    onSelect,
    notesAttachments,
    previewStyle = "large",
    dateProp,
    view,
    extraBottomPadding,
    footer,
  } = props;
  const setNoteOrder = useSetNoteOrder();
  // A small activation distance so a tap still opens the note — only a real
  // drag starts a reorder.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      if (notesMap.get(active.id as string)?.sharedWithMe) return;
      const oldIndex = noteIds.indexOf(active.id as string);
      const newIndex = noteIds.indexOf(over.id as string);
      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = arrayMove(noteIds, oldIndex, newIndex);
      const effOrder = (idx: number): number | undefined => {
        const note = idx >= 0 && idx < reordered.length ? notesMap.get(reordered[idx]) : undefined;
        return note ? noteEffectiveOrder(note) : undefined;
      };
      // List is DESC (higher = top), so the note now above has the larger key.
      const sortOrder = manualOrderBetween(effOrder(newIndex - 1), effOrder(newIndex + 1));
      setNoteOrder.mutate({ id: active.id as string, sortOrder });
    },
    [noteIds, notesMap, setNoteOrder],
  );

  const scrollRef = useRef<HTMLDivElement>(null);
  useRevealActiveNote(activeNoteId, noteIds, (index) =>
    scrollRef.current
      ?.querySelector(`[data-note-id="${noteIds[index]}"]`)
      ?.scrollIntoView({ block: "nearest" }),
  );

  if (noteIds.length === 0) {
    return (
      <div className="flex flex-col flex-1 overflow-y-auto p-2">
        <EmptyNoteList view={view} />
      </div>
    );
  }

  return (
    <div ref={scrollRef} className={cn("flex-1 overflow-y-auto p-2", extraBottomPadding && "pb-24")}>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={noteIds} strategy={verticalListSortingStrategy}>
          {noteIds.map((id, i) => {
            const note = notesMap.get(id);
            if (!note) return null;
            const isActive = id === activeNoteId;
            const isSelected = selectedIds.has(id);
            const attachments = notesAttachments?.get(id);
            const attachmentsKey = attachments
              ? attachments.map((a) => `${a.kind[0]}${a.src}`).join("|")
              : "";
            const nextId = noteIds[i + 1];
            const prevSelected = i > 0 && selectedIds.has(noteIds[i - 1]);
            const nextSelected = !!nextId && selectedIds.has(nextId);
            const nextHighlighted =
              !!nextId && (nextId === activeNoteId || selectedIds.has(nextId));
            return (
              <SortableNoteRow
                key={id}
                id={id}
                note={note}
                isActive={isActive}
                isSelected={isSelected}
                isSelectionMode={isSelectionMode}
                onDelete={onDelete}
                onSelect={onSelect}
                previewStyle={previewStyle}
                attachments={attachments}
                attachmentsKey={attachmentsKey}
                mergesTop={isSelected && prevSelected}
                mergesBottom={isSelected && nextSelected}
                showSeparatorBelow={!!nextId && !(isActive || isSelected) && !nextHighlighted}
                dateProp={dateProp}
              />
            );
          })}
        </SortableContext>
      </DndContext>
      {footer}
    </div>
  );
}

/* ── NoteList ────────────────────────────────────────────── */
export function NoteList({
  tagId,
  view,
  leftAction,
  bottomInset,
}: NoteListProps) {
  const { t } = useTranslation();
  const isMobileLayout = useIsMobile();
  // IDs for ordering + batch map for note data (single subscription)
  const noteIds = useNoteIds(tagId, view);
  const notesMap = useNotesMap(noteIds);
  const allTags = useTags();

  // Shared-with-me stubs (read-only, not in `notes`) — merged over the notes
  // map so All Notes can render them. Own notes always win on id collision.
  const sharedStubs = useSharedStubsMap();
  const combinedNotesMap = useMemo(() => {
    if (sharedStubs.size === 0) return notesMap;
    const merged = new Map(notesMap);
    for (const [id, stub] of sharedStubs) {
      if (!merged.has(id)) merged.set(id, stub);
    }
    return merged;
  }, [notesMap, sharedStubs]);
  // Bulk selection only applies to own notes — shared stubs are excluded.
  const selectableIds = useMemo(
    () =>
      sharedStubs.size === 0
        ? noteIds
        : noteIds.filter((id) => !sharedStubs.has(id)),
    [noteIds, sharedStubs],
  );

  // Single batched query for all visible notes' attachments (image thumbnails +
  // file chips in the row). One subscription for the whole list — never a
  // per-row query (see docs/REACTIVE_QUERIES.md). Capped per note to bound work.
  const attachmentsByNote =
    useSQLiteQuery<Map<string, ProjectedAttachment[]>>(
      () => {
        const map = new Map<string, ProjectedAttachment[]>();
        if (noteIds.length === 0) return map;
        const placeholders = noteIds.map(() => "?").join(",");
        const rows = query<{
          note_id: string;
          position: number;
          user_id: string;
          kind: string;
          alt: string;
          src: string;
          created_at: string;
          updated_at: string;
        }>(
          `SELECT note_id, position, user_id, kind, alt, src, created_at, updated_at
           FROM attachment_projection
           WHERE note_id IN (${placeholders})
           ORDER BY note_id, position`,
          noteIds,
        );
        for (const row of rows) {
          const existing = map.get(row.note_id);
          if (existing && existing.length >= NOTE_ATTACHMENT_CAP) continue;
          const att: ProjectedAttachment = {
            noteId: row.note_id,
            userId: row.user_id,
            position: row.position,
            kind: row.kind === "file" ? "file" : "image",
            alt: row.alt,
            src: row.src,
            createdAt: row.created_at,
            updatedAt: row.updated_at,
          };
          if (existing) existing.push(att);
          else map.set(row.note_id, [att]);
        }
        return map;
      },
      [noteIds.join(",")],
      ["attachment_projection"],
      new Map<string, ProjectedAttachment[]>(),
    ) ?? new Map<string, ProjectedAttachment[]>();
  const notePreviewStyle = useUIStore((s) => s.notePreviewStyle);
  const noteSortField = useUIStore((s) => s.noteSortField);
  const newNoteBinding = useShortcutsStore((s) => s.resolved["app.newNote"]);

  const { id: activeNoteId } = useParams<{ id?: string }>();
  const createNote = useCreateNote();
  const deleteNote = useDeleteNote();
  const navigate = useNavigate();
  // Free note limit count (from 80%), a quiet line at the end of the list.
  // "56 of 50" is a legitimate state: the server never rejects a create, so
  // MCP / another device can overshoot.
  const noteLimit = useNoteLimit();
  const noteLimitFooter = noteLimit.nearLimit && noteLimit.limit != null && (
    <p className="py-3 text-center text-xs text-muted">
      {t("noteLimit.banner", { count: noteLimit.count, limit: noteLimit.limit })}
    </p>
  );

  const [searchParams] = useSearchParams();
  const activeTag = tagId ? allTags.find((tg) => tg.id === tagId) : undefined;
  const [renameTagOpen, setRenameTagOpen] = useState(false);
  const [appearanceTagOpen, setAppearanceTagOpen] = useState(false);
  useNoteListNav(noteIds);

  // Selection state
  const selectedIds = useSelectionStore((s) => s.selectedIds);
  const clearSelection = useSelectionStore((s) => s.clearSelection);
  const toggle = useSelectionStore((s) => s.toggle);
  const rangeSelect = useSelectionStore((s) => s.rangeSelect);
  const selectAll = useSelectionStore((s) => s.selectAll);
  const setSelectionMode = useSelectionStore((s) => s.setSelectionMode);
  const isSelectionMode = useSelectionStore((s) => s.isSelectionMode);
  const hasSelection = selectedIds.size > 0;

  // Export every note in view (no manual selection). Same Pro-gated bulk-export
  // path as BulkActionsBar; NoteListMenu only renders outside Trash/Archive.
  const isPro = useIsPro();
  const openPaywall = useUIStore((s) => s.openPaywall);
  const handleExportAll = useCallback(
    async (format: ExportFormat) => {
      if (!isPro) {
        openPaywall("bulk_export");
        return;
      }
      try {
        await bulkExport(selectableIds, format);
      } catch (err) {
        Sentry.captureException(err);
      }
    },
    [isPro, openPaywall, selectableIds],
  );

  // Clear selection when view/tag changes
  useEffect(() => {
    clearSelection();
  }, [tagId, view, clearSelection]);

  // Keyboard shortcuts: Cmd+A to select all, Escape to clear
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Don't intercept when user is typing in an input, textarea, or the editor
      const el = document.activeElement;
      if (
        el instanceof HTMLInputElement ||
        el instanceof HTMLTextAreaElement ||
        el?.closest(".ProseMirror") ||
        el?.closest("[role='listbox']") ||
        el?.closest("[data-radix-popper-content-wrapper]")
      )
        return;

      if (e.key === "Escape" && (hasSelection || isSelectionMode)) {
        e.preventDefault();
        clearSelection();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key === "a" && selectableIds.length > 0) {
        e.preventDefault();
        selectAll(selectableIds);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [hasSelection, isSelectionMode, selectableIds, clearSelection, selectAll]);

  const handleSelect = useCallback(
    (noteId: string, e: React.MouseEvent | null) => {
      if (sharedStubs.has(noteId)) return;
      if (e?.shiftKey) {
        rangeSelect(noteId, selectableIds);
      } else {
        toggle(noteId);
      }
    },
    [selectableIds, sharedStubs, rangeSelect, toggle],
  );

  const handleDelete = (noteId: string) => {
    deleteNote.mutate(noteId);
    if (noteId === activeNoteId) {
      const qs = searchParams.toString();
      navigate(`/app${qs ? `?${qs}` : ""}`, { replace: true });
    }
  };

  const handleBulkDeleteActiveNote = useCallback(() => {
    if (activeNoteId && selectedIds.has(activeNoteId)) {
      const qs = searchParams.toString();
      navigate(`/app${qs ? `?${qs}` : ""}`, { replace: true });
    }
  }, [activeNoteId, selectedIds, navigate, searchParams]);

  const handleCreate = async () => {
    const content = newNoteContent(
      useUIStore.getState().newNoteStart,
      newNoteTagPath(tagId, allTags),
      new Date(),
    );
    const tags = tagId ? [tagId] : undefined;
    const note = await createNote.mutateAsync(
      { title: extractTitleFromContent(content), content, tags },
      "note_list",
    );
    // null: the free note limit is reached and the paywall is already open.
    if (!note) return;
    useUIStore.getState().setPendingNewNoteId(note.id);
    const localNote = getNote(note.id);
    if (localNote) setPrefetchedNote(localNote);
    const qs = searchParams.toString();
    navigate(`/app/note/${note.id}${qs ? `?${qs}` : ""}`);
  };

  const title = useMemo(() => {
    if (tagId) {
      const tag = allTags.find((tg) => tg.id === tagId);
      if (tag) {
        const byId = new Map(allTags.map((tg) => [tg.id, tg]));
        return `#${tagFullPath(tag, byId)}`;
      }
      return t("views.all");
    }
    return view ? t(VIEW_LABEL_KEYS[view] ?? "views.all") : t("views.all");
  }, [tagId, allTags, view, t]);

  return (
    <div className="relative flex h-full flex-col font-chrome">
      {hasSelection || isSelectionMode ? (
        <BulkActionsBar onDeleteActiveNote={handleBulkDeleteActiveNote} />
      ) : (
        (() => {
          const menu = (align?: "start" | "end") => (
            <NoteListMenu
              align={align}
              title={title}
              noteCount={noteIds.length}
              onSelectNotes={
                selectableIds.length > 0
                  ? () => {
                      setSelectionMode(true);
                    }
                  : undefined
              }
              onExportAll={
                selectableIds.length > 0 ? handleExportAll : undefined
              }
              tagId={activeTag ? tagId : undefined}
              onRenameTag={activeTag ? () => setRenameTagOpen(true) : undefined}
              onEditTagAppearance={
                activeTag ? () => setAppearanceTagOpen(true) : undefined
              }
            />
          );
          const search = (
            <Button
              variant="outline"
              size="header"
              onClick={() => {
                // Phones: keeps the keyboard able to open in the palette.
                if (isMobileLayout) holdKeyboard();
                useUIStore.getState().openCmdPalette();
              }}
              title={t("toolbar.searchNotes")}
              aria-label={t("toolbar.searchNotes")}
            >
              <Icon name="search" size={18} className="text-ink" />
            </Button>
          );
          // Phones (iOS): sidebar · view title · search and sort; New Note
          // floats bottom right. Desktop (Mac): the sort menu on the left,
          // search and New Note on the right, no title.
          return isMobileLayout ? (
            <NoteColumnHeader
              title={title}
              titleContent={
                activeTag ? (
                  <TagTitle tag={activeTag} path={title.slice(1)} iconSize={18} />
                ) : undefined
              }
              leftAction={leftAction}
              action={
                <div className="flex items-center gap-2">
                  {search}
                  {menu("end")}
                </div>
              }
            />
          ) : (
            <NoteColumnHeader
              leftAction={
                <div className="flex items-center gap-2">
                  {leftAction}
                  {menu()}
                </div>
              }
              action={
                <div className="flex items-center gap-2">
                  {search}
                  <Button
                    size="header"
                    onClick={handleCreate}
                    disabled={createNote.isPending}
                    title={
                      newNoteBinding
                        ? t("noteList.newNoteShortcut", { shortcut: formatKeyCombo(newNoteBinding) })
                        : t("toolbar.newNote")
                    }
                    aria-label={t("toolbar.newNote")}
                    className={ACCENT_GLASS}
                  >
                    <Icon name="plus" size={20} />
                  </Button>
                </div>
              }
            />
          );
        })()
      )}

      {/* Manual sort → drag-to-reorder list (non-virtualized). Disabled while
          selecting, so bulk selection keeps the normal virtualized list. */}
      {noteSortField === "manual" && !(isSelectionMode || hasSelection) ? (
        <SortableNoteList
          noteIds={noteIds}
          notesMap={combinedNotesMap}
          activeNoteId={activeNoteId}
          selectedIds={selectedIds}
          isSelectionMode={isSelectionMode || hasSelection}
          onDelete={handleDelete}
          onSelect={handleSelect}
          notesAttachments={attachmentsByNote}
          previewStyle={notePreviewStyle}
          dateProp="updatedAt"
          view={view}
          extraBottomPadding={bottomInset}
          footer={noteLimitFooter}
        />
      ) : (
        <VirtualizedNoteList
          noteIds={noteIds}
          notesMap={combinedNotesMap}
          activeNoteId={activeNoteId}
          selectedIds={selectedIds}
          isSelectionMode={isSelectionMode || hasSelection}
          onDelete={handleDelete}
          onSelect={handleSelect}
          notesAttachments={attachmentsByNote}
          previewStyle={notePreviewStyle}
          dateProp={noteSortField === "createdAt" ? "createdAt" : "updatedAt"}
          view={view}
          extraBottomPadding={bottomInset}
          footer={noteLimitFooter}
        />
      )}

      {/* Phones: New Note is a big + floating bottom right, as on iOS; the
          list ends with room for it. */}
      {bottomInset && !(hasSelection || isSelectionMode) && (
        <Button
          onClick={handleCreate}
          disabled={createNote.isPending}
          aria-label={t("toolbar.newNote")}
          className={`absolute right-4 bottom-[max(16px,env(safe-area-inset-bottom))] z-20 h-14 w-14 rounded-full p-0 shadow-lg ${ACCENT_GLASS}`}
        >
          <Icon name="plus" size={26} />
        </Button>
      )}

      {/* Tag actions from the note-list title menu (all viewports). */}
      <Dialog
        open={appearanceTagOpen && !!activeTag}
        onOpenChange={setAppearanceTagOpen}
      >
        {activeTag && (
          <DialogContent className="max-w-xs p-0">
            <DialogHeader className="px-3 py-2 border-b border-border-light">
              <DialogTitle className="text-xs font-semibold text-ink-secondary">
                <TagTitle
                  tag={activeTag}
                  path={tagFullPath(activeTag, new Map(allTags.map((tg) => [tg.id, tg])))}
                />
              </DialogTitle>
            </DialogHeader>
            <TagEditor tag={activeTag} />
          </DialogContent>
        )}
      </Dialog>
      <Dialog
        open={renameTagOpen && !!activeTag}
        onOpenChange={setRenameTagOpen}
      >
        {activeTag && (
          <TagRenameDialog
            tag={activeTag}
            allTags={allTags}
            onClose={() => setRenameTagOpen(false)}
          />
        )}
      </Dialog>
    </div>
  );
}
